import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Kolom } from '@xxtract/tabelfilters';
import { StatusBadge, TreeTable, type TreeNode, type TreeTableColumn } from '@xxtract/ui';
import { ariaSort, Filterbalk, SorteerbareKop, useTabelData, useTabelStand } from '@/components/tabel';
import { ProductImage } from '@/features/completeness/components/ProductImage';
import { formatOrderEventTimestamp } from '../format';
import { processStepBadge, serviceStatusBadge, type BadgeSpec } from '../tree-badges';
import { laatsteActiviteit, ORDERBOOM_LIJST, orderBoomSleutel, type OrderBoomContext } from '../ordersLijst';
import type { OrderTree, PackagingLayer, ProductNode, TreeServiceView } from '../orders.api';

interface OrderTreeViewProps {
  tree: OrderTree;
  locale: string;
  onOpenGtinDetail: (tradeItemId: string) => void;
}

/**
 * Kolombreedtes: kop en cellen delen ze (TreeTable), in px zodat het raster vast ligt. De eerste kolom
 * draagt ook de pijl en het icoon van een rij (52px) en de inspringing van een laag.
 * NB: Tailwind ziet alleen volledige, letterlijke klassen; niet samenstellen uit stukken.
 */
const BREEDTE: Record<string, string> = {
  naam: 'w-[240px] shrink-0',
  gtin: 'w-[110px] shrink-0',
  processtap: 'w-[130px] shrink-0',
  servicestatus: 'w-[130px] shrink-0',
  laatsteEvent: 'w-[110px] shrink-0',
};
const NO_DATA_BADGE: BadgeSpec = { status: 'process-no-data', labelKey: 'orders.processStep.noData' };

/** A product/layer row shows a single, representative service's badges (h3-3 mockup shows one
 * processtap/servicestatus pair per row) — the first service, deterministically. */
function primaryService(services: TreeServiceView[]): TreeServiceView | null {
  return services[0] ?? null;
}

function badgeCells(services: TreeServiceView[], t: TFunction): Pick<Record<string, ReactNode>, 'processtap' | 'servicestatus'> {
  const service = primaryService(services);
  const stepBadge = service != null ? processStepBadge(service) : NO_DATA_BADGE;
  const statusBadge = service != null ? serviceStatusBadge(service.serviceStatus) : NO_DATA_BADGE;
  return {
    processtap: <div className="min-w-0 overflow-hidden"><StatusBadge className="max-w-full truncate" label={t(stepBadge.labelKey, stepBadge.labelParams)} status={stepBadge.status} /></div>,
    servicestatus: <div className="min-w-0 overflow-hidden"><StatusBadge className="max-w-full truncate" label={t(statusBadge.labelKey)} status={statusBadge.status} /></div>,
  };
}

function productCells(product: ProductNode, locale: string, t: TFunction): Record<string, ReactNode> {
  const activity = laatsteActiviteit(product.services);
  return {
    naam: (
      <div className="flex min-w-0 items-center gap-2">
        <ProductImage gln={product.gln} gtin={product.gtin} productName={product.description ?? product.gtin} variant="thumbnail" />
        <span className="truncate font-medium text-[var(--foreground)]">{product.description ?? t('orders.tree.unnamedProduct')}</span>
      </div>
    ),
    gtin: <span className="truncate text-xs text-[var(--foreground-muted)]">{product.gtin}</span>,
    ...badgeCells(product.services, t),
    laatsteEvent: <span className="truncate text-xs text-[var(--foreground-muted)]">{activity != null ? formatOrderEventTimestamp(activity, locale) : t('orders.tree.noActivity')}</span>,
  };
}

function layerCells(layer: PackagingLayer, locale: string, t: TFunction): Record<string, ReactNode> {
  const activity = laatsteActiviteit(layer.services);
  return {
    naam: <span className="truncate font-medium text-[var(--foreground)]">{layer.description ?? t('orders.tree.unnamedLayer')}</span>,
    gtin: <span className="truncate text-xs text-[var(--foreground-muted)]">{layer.gtin}</span>,
    ...badgeCells(layer.services, t),
    laatsteEvent: <span className="truncate text-xs text-[var(--foreground-muted)]">{activity != null ? formatOrderEventTimestamp(activity, locale) : t('common.notAvailable')}</span>,
  };
}

function buildProductLabel(product: ProductNode, t: TFunction): string {
  return `${product.description ?? t('orders.tree.unnamedProduct')} ${product.gtin}`;
}

function buildLayerLabel(layer: PackagingLayer, t: TFunction): string {
  return `${layer.description ?? t('orders.tree.unnamedLayer')} ${layer.gtin}`;
}

/** FR17: order -> products -> verpakkingslagen. Default: producten open, lagen dicht (EXPERIENCE.md).
 * spec-tabelfilters story 11: centrale filters en sortering in de browser over de producten (lagen blijven
 * onder hun product, in hun eigen volgorde), koppen binnen de boomtabel, stand per order. */
export function OrderTreeView({ tree, locale, onOpenGtinDetail }: OrderTreeViewProps) {
  const { t } = useTranslation();
  const stand = useTabelStand(ORDERBOOM_LIJST, orderBoomSleutel(tree.orderNumber));
  const context = useMemo<OrderBoomContext>(() => ({ t: (s, opties) => (opties ? t(s, opties) : t(s)) }), [t]);
  const data = useTabelData(tree.products, ORDERBOOM_LIJST, stand, context);
  const ariaLabel = t('orders.tree.ariaLabel', { orderNumber: tree.orderNumber });

  const columns: TreeTableColumn[] = ORDERBOOM_LIJST.kolommen.map((k) => ({
    id: k.sleutel,
    label: t(k.kopSleutel),
    header: <SorteerbareKop kolom={k as Kolom<never, never>} onKlik={() => stand.klikOpKop(k.sleutel)} sortering={stand.sortering} />,
    ariaSort: ariaSort(stand.sortering, k.sleutel),
    className: BREEDTE[k.sleutel]!,
  }));

  const nodes: TreeNode[] = data.rijen.map((product) => ({
    id: product.tradeItemId,
    label: buildProductLabel(product, t),
    cells: productCells(product, locale, t),
    icon: <span aria-hidden="true" className="material-symbols-outlined text-[16px]">inventory_2</span>,
    defaultExpanded: true,
    children: product.packagingLayers.map((layer) => ({
      id: layer.tradeItemId ?? `${product.tradeItemId}-${layer.gtin}`,
      label: buildLayerLabel(layer, t),
      cells: layerCells(layer, locale, t),
      icon: <span aria-hidden="true" className="material-symbols-outlined text-[16px]">layers</span>,
    })),
  }));

  return (
    <div className="space-y-3">
      <div className="px-4 pt-3">
        <Filterbalk context={context} data={data} lijst={ORDERBOOM_LIJST} stand={stand} />
      </div>
      {nodes.length === 0 ? (
        <p className="px-4 pb-3 text-xs text-[var(--foreground-muted)]" data-testid="order-tree-geen-treffers">{t('tabel.geenTreffers')}</p>
      ) : (
        // Kop en rijen in één scrollvak (de kop staat binnen de boomtabel, CAP-12): op 375 px schuift de boom in
        // zijn eigen vak en niet de pagina. Focusbaar, omdat de rijen pijl-links/rechts zelf afvangen; omlijning met !
        // omdat de globale *:focus-visible-regel anders wint.
        <div aria-label={ariaLabel} className="overflow-x-auto focus-visible:outline-2! focus-visible:-outline-offset-2! focus-visible:outline-[var(--secondary)]!" role="region" tabIndex={0}>
          <div className="min-w-max">
            <TreeTable
              ariaLabel={ariaLabel}
              className="overflow-x-visible"
              columns={columns}
              data-testid="order-tree"
              nodes={nodes}
              onOpen={(node) => {
                const product = tree.products.find((candidate) => candidate.tradeItemId === node.id);
                if (product != null) onOpenGtinDetail(product.tradeItemId);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
